import { useState } from "react";
import { Box, Typography } from "@mui/material";
import SectionCard from "../../Components/Back-End/SectionCard";
import GenericTable from "../../Components/Back-End/Table";

export function HomePageCarouselTable() {
    const [validationErrors, setValidationErrors] = useState({});

    const columns = [
        { accessorKey: "id", header: "ID", enableEditing: false },

        {
            accessorKey: "imageUrl",
            header: "Image",
            Edit: ({ cell, row }) => {
                const [previewUrl, setPreviewUrl] = useState(null);
                const [error, setError] = useState("");
                const filename = cell.getValue();
                const existingUrl = filename ? `fsc-images/homepage-carousel/${filename}` : null;

                const handleFileChange = (event) => {
                    const file = event.target.files[0];
                    if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                            setError("File size must be under 5 MB");
                            event.target.value = "";
                            setPreviewUrl(null);
                            return;
                        }
                        setError("");
                        const reader = new FileReader();
                        reader.onloadend = () => setPreviewUrl(reader.result);
                        reader.readAsDataURL(file);
                        row._valuesCache["imageFile"] = file;
                    }
                };

                return (
                    <Box display="flex" flexDirection="column" gap={2}>
                        {(previewUrl || existingUrl) && (
                            <img
                                src={previewUrl || existingUrl}
                                alt="Preview"
                                style={{
                                    width: "100%",
                                    maxWidth: "400px",
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                    border: "2px solid #ccc",
                                    marginBottom: "1rem",
                                }}
                            />
                        )}

                        <Box>
                            <label style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                Upload Image
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    style={{ display: "block", marginTop: "0.25rem" }}
                                />
                            </label>
                            {error && (
                                <Typography variant="caption" color="error">
                                    {error}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            },
            Cell: ({ cell }) => {
                const filename = cell.getValue();
                const imagePath = filename ? `fsc-images/homepage-carousel/${filename}` : "";
                return (
                    <img
                        src={imagePath}
                        alt="Carousel Slide"
                        style={{
                            width: "120px",
                            height: "60px",
                            objectFit: "cover",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                        }}
                    />
                );
            },
        },

        {
            accessorKey: "title",
            header: "Title",
            muiEditTextFieldProps: () => ({
                required: true,
                error: !!validationErrors.title,
                helperText: validationErrors.title ?? "",
                onChange: () => setValidationErrors((prev) => ({ ...prev, title: undefined })),
            }),
            validate: (value) => {
                if (!value || value.trim() === "") return "Title is required.";
            },
        },
        {
            accessorKey: "tabName",
            header: "Tab Name",
            meta: { hideInTable: true },
            muiEditTextFieldProps: () => ({
                required: true,
                error: !!validationErrors.tabName,
                helperText: validationErrors.tabName ?? "",
                onChange: () => setValidationErrors((prev) => ({ ...prev, tabName: undefined })),
            }),
            validate: (value) => {
                if (!value || value.trim() === "") return "Tab Name is required.";
            },
        },
        {
            accessorKey: "subtitle",
            header: "Subtitle",
            meta: { hideInTable: true },
            muiEditTextFieldProps: () => ({
                error: !!validationErrors.subtitle,
                helperText: validationErrors.subtitle ?? "",
                onChange: () => setValidationErrors((prev) => ({ ...prev, subtitle: undefined })),
            }),
        },
        {
            accessorKey: "buttonLink",
            header: "Button Link",
            meta: { hideInTable: true },
            muiEditTextFieldProps: () => ({
                error: !!validationErrors.buttonLink,
                helperText: validationErrors.buttonLink ?? "",
                onChange: () => setValidationErrors((prev) => ({ ...prev, buttonLink: undefined })),
            }),
        },
        {
            accessorKey: "sortOrder",
            header: "Sort Order",
            muiEditTextFieldProps: () => ({
                type: "number",
                required: true,
                error: !!validationErrors.displayOrder,
                helperText: validationErrors.displayOrder ?? "",
                onChange: () => setValidationErrors((prev) => ({ ...prev, displayOrder: undefined })),
            }),
            validate: (value) => {
                if (value === undefined || value === null || value === "") {
                    return "Display order is required.";
                }
                if (isNaN(value)) return "Display order must be a number.";
                if (Number(value) < 0) return "Display order must be >= 0.";
            },
        },
        {
            accessorKey: "isActive",
            header: "Active",
            editVariant: "select",
            editSelectOptions: [
                { label: "Yes", value: true },
                { label: "No", value: false },
            ],
            muiEditTextFieldProps: () => ({
                select: true,
                required: true,
                defaultValue: true,
                error: !!validationErrors.isActive,
                helperText: validationErrors.isActive ?? "",
                onChange: () => setValidationErrors((prev) => ({ ...prev, isActive: undefined })),
            }),
            validate: (value) => {
                if (value === undefined || value === null || value === "") {
                    return "Active status is required.";
                }
            },
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value ? "Yes" : "No";
            },
        },
    ];

    const columnVisibility = columns.reduce((acc, col) => {
        if (col.meta?.hideInTable) {
            acc[col.accessorKey] = false;
        }
        return acc;
    }, {});

    return (
        <SectionCard
            title="Home Page Carousel"
            subtitle="Manage your homepage carousel slides below"
        >
            <GenericTable
                columns={columns}
                itemName="Carousel Slide"
                createApi="/api/homepagecarousel/save"
                readApi="/api/homepagecarousel/GetCarouselItems"
                updateApi="/api/homepagecarousel/save"
                deleteApi="/api/homepagecarousel/delete"
                columnVisibility={columnVisibility}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
                publicSitePage="home"
            />
        </SectionCard>
    );
}
